
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import type { Profile } from '@/lib/types';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getRoleDisplayName, getRoleBadgeColor } from '@/lib/roleUtils';
import { UserEmailConfiguration } from '@/components/profile/UserEmailConfiguration';
import { UserSenderIdentities } from '@/components/profile/UserSenderIdentities';
import { TaskEmailPreferences } from '@/components/profile/TaskEmailPreferences';

const TIMEZONES = [
  { value: 'UTC-12:00', label: '(UTC-12:00) International Date Line West' },
  { value: 'UTC-11:00', label: '(UTC-11:00) Samoa, Midway Island' },
  { value: 'UTC-10:00', label: '(UTC-10:00) Hawaii' },
  { value: 'UTC-09:00', label: '(UTC-09:00) Alaska' },
  { value: 'UTC-08:00', label: '(UTC-08:00) Pacific Time (US & Canada)' },
  { value: 'UTC-07:00', label: '(UTC-07:00) Mountain Time (US & Canada)' },
  { value: 'UTC-06:00', label: '(UTC-06:00) Central Time (US & Canada)' },
  { value: 'UTC-05:00', label: '(UTC-05:00) Eastern Time (US & Canada)' },
  { value: 'UTC-04:00', label: '(UTC-04:00) Atlantic Time (Canada)' },
  { value: 'UTC-03:00', label: '(UTC-03:00) Brasilia, Buenos Aires' },
  { value: 'UTC-02:00', label: '(UTC-02:00) Mid-Atlantic' },
  { value: 'UTC-01:00', label: '(UTC-01:00) Azores, Cape Verde Islands' },
  { value: 'UTC+00:00', label: '(UTC+00:00) London, Dublin, Lisbon' },
  { value: 'UTC+01:00', label: '(UTC+01:00) Berlin, Paris, Rome, Madrid' },
  { value: 'UTC+02:00', label: '(UTC+02:00) Athens, Istanbul, Cairo' },
  { value: 'UTC+03:00', label: '(UTC+03:00) Moscow, Kuwait, Riyadh' },
  { value: 'UTC+04:00', label: '(UTC+04:00) Abu Dhabi, Dubai, Baku' },
  { value: 'UTC+05:00', label: '(UTC+05:00) Islamabad, Karachi, Tashkent' },
  { value: 'UTC+05:30', label: '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi' },
  { value: 'UTC+06:00', label: '(UTC+06:00) Dhaka, Almaty, Novosibirsk' },
  { value: 'UTC+07:00', label: '(UTC+07:00) Bangkok, Hanoi, Jakarta' },
  { value: 'UTC+08:00', label: '(UTC+08:00) Beijing, Hong Kong, Singapore' },
  { value: 'UTC+09:00', label: '(UTC+09:00) Tokyo, Seoul, Osaka' },
  { value: 'UTC+10:00', label: '(UTC+10:00) Sydney, Melbourne, Brisbane' },
  { value: 'UTC+11:00', label: '(UTC+11:00) Magadan, Solomon Islands' },
  { value: 'UTC+12:00', label: '(UTC+12:00) Auckland, Wellington, Fiji' },
];

const personalInfoSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
});

const workInfoSchema = z.object({
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  officeLocation: z.string().optional(),
  workEmail: z.string().email("Invalid email address").optional().or(z.literal('')),
  workPhone: z.string().optional(),
  contactNumber: z.string().optional(),
  linkedinUrl: z.string().url("Must be a valid URL").optional().or(z.literal('')),
  bio: z.string().max(500, "Bio must be 500 characters or less").optional(),
  timezone: z.string().optional(),
});

const emailPreferencesSchema = z.object({
  preferredFromName: z.string().optional(),
  emailSignature: z.string().max(1000, "Signature must be 1000 characters or less").optional(),
});

export default function Profile() {
  const { user, profile, isLoading, signOut, setProfile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmittingPersonal, setIsSubmittingPersonal] = useState(false);
  const [isSubmittingWork, setIsSubmittingWork] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  
  const personalForm = useForm({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      firstName: profile?.first_name || '',
      lastName: profile?.last_name || '',
    },
  });

  const workForm = useForm({
    resolver: zodResolver(workInfoSchema),
    defaultValues: {
      jobTitle: profile?.job_title || '',
      department: profile?.department || '',
      officeLocation: profile?.office_location || '',
      workEmail: profile?.work_email || '',
      workPhone: profile?.work_phone || '',
      contactNumber: (profile as any)?.contact_number || '',
      linkedinUrl: profile?.linkedin_url || '',
      bio: profile?.bio || '',
      timezone: profile?.timezone || '',
    },
  });

  const emailPreferencesForm = useForm({
    resolver: zodResolver(emailPreferencesSchema),
    defaultValues: {
      preferredFromName: profile?.preferred_from_name || '',
      emailSignature: profile?.email_signature || '',
    },
  });

  useEffect(() => {
    if (profile) {
      personalForm.reset({
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
      });
      
      workForm.reset({
        jobTitle: profile.job_title || '',
        department: profile.department || '',
        officeLocation: profile.office_location || '',
        workEmail: profile.work_email || '',
        workPhone: profile.work_phone || '',
        contactNumber: (profile as any)?.contact_number || '',
        linkedinUrl: profile.linkedin_url || '',
        bio: profile.bio || '',
        timezone: profile.timezone || '',
      });

      emailPreferencesForm.reset({
        preferredFromName: profile.preferred_from_name || '',
        emailSignature: profile.email_signature || '',
      });
    }
  }, [profile]);
  
  const handleUpdatePersonalInfo = async (data: z.infer<typeof personalInfoSchema>) => {
    if (!user) return;
    
    setIsSubmittingPersonal(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: data.firstName,
          last_name: data.lastName,
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast({
        title: "Profile updated",
        description: "Your personal information has been updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Update failed",
        description: error.message || "Could not update profile",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingPersonal(false);
    }
  };

  const handleUpdateWorkInfo = async (data: z.infer<typeof workInfoSchema>) => {
    if (!user) return;
    
    setIsSubmittingWork(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          job_title: data.jobTitle,
          department: data.department,
          office_location: data.officeLocation,
          work_email: data.workEmail,
          work_phone: data.workPhone,
          contact_number: data.contactNumber,
          linkedin_url: data.linkedinUrl,
          bio: data.bio,
          timezone: data.timezone,
        } as any)
        .eq('id', user.id);
      
      if (error) throw error;

      // Fetch fresh profile data after update
      const { data: freshProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (!fetchError && freshProfile) {
        setProfile(freshProfile as Profile);
      }
      
      toast({
        title: "Work info updated",
        description: "Your work information has been updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Update failed",
        description: error.message || "Could not update work information",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingWork(false);
    }
  };

  const handleUpdateEmailPreferences = async (data: z.infer<typeof emailPreferencesSchema>) => {
    if (!user) return;
    
    setIsSubmittingEmail(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          preferred_from_name: data.preferredFromName,
          email_signature: data.emailSignature,
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast({
        title: "Email preferences updated",
        description: "Your email preferences have been updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Update failed",
        description: error.message || "Could not update email preferences",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingEmail(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 w-full mx-auto px-4 py-6 md:py-8 lg:py-12">
          <div className="w-full max-w-[90%] md:max-w-3xl lg:max-w-4xl mx-auto">
            <Card className="glass-card overflow-hidden animate-pulse">
              <div className="p-8 space-y-6">
                <div className="flex flex-col items-center space-y-4">
                  <div className="h-24 w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 rounded-full bg-white/10" />
                  <div className="h-6 w-48 bg-white/10 rounded" />
                  <div className="h-4 w-64 bg-white/10 rounded" />
                </div>
                <div className="h-12 bg-white/10 rounded" />
                <div className="space-y-4">
                  <div className="h-24 bg-white/10 rounded" />
                  <div className="h-24 bg-white/10 rounded" />
                  <div className="h-24 bg-white/10 rounded" />
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>
    );
  }
  
  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    return user?.email?.substring(0, 2).toUpperCase() || 'US';
  };
  
  const handleLogout = async () => {
    await signOut();
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-background will-change-scroll">
      <main className="flex-1 w-full mx-auto px-4 py-6 md:py-8 lg:py-12">
        <div className="w-full max-w-[90%] md:max-w-3xl lg:max-w-4xl mx-auto">
          <Card className="glass-card overflow-hidden animate-fade-in">
            <CardHeader className="relative p-6 md:p-8 lg:p-10 border-b border-border">
              {/* Logout button - absolute positioned top-right */}
              <Button 
                variant="destructive" 
                className="absolute top-6 right-6 shadow-md hover:shadow-lg transition-shadow" 
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" /> Log Out
              </Button>

              <div className="flex flex-col items-center text-center space-y-4">
                <Avatar className="h-24 w-24 md:h-28 md:w-28 lg:h-32 lg:w-32 border-4 border-white/10 shadow-2xl ring-2 ring-primary/20 transition-all duration-300">
                  <AvatarImage src={profile?.avatar_url || ''} />
                  <AvatarFallback className="text-2xl font-bold bg-gradient-to-br from-purple-500 to-purple-700 text-white">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                
                <div className="space-y-2 max-w-md">
                  <h2 className="text-2xl font-bold">
                    {profile?.first_name ? `${profile.first_name} ${profile.last_name}` : 'User'}
                  </h2>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <div className="flex items-center justify-center gap-2">
                    <span className={`text-xs px-3 py-1.5 rounded-full font-medium ${getRoleBadgeColor(profile?.role)}`}>
                      {getRoleDisplayName(profile?.role)}
                    </span>
                  </div>
                  {profile?.job_title && (
                    <p className="text-sm text-muted-foreground">
                      {profile.job_title}{profile.department ? ` • ${profile.department}` : ''}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-8">
              <Tabs defaultValue="personal" className="w-full flex flex-col" style={{ minHeight: 'calc(100vh - 400px)' }} onValueChange={() => {
                setTimeout(() => {
                  const activeTab = document.querySelector('[data-state="active"]');
                  if (activeTab) activeTab.scrollTop = 0;
                }, 0);
              }}>
                <TabsList className="sticky top-0 z-10 mb-6 w-full inline-flex justify-start sm:justify-center bg-white/[0.03] backdrop-blur-md p-1.5 sm:p-2 rounded-lg shadow-md border border-border overflow-x-auto scrollbar-hide snap-x snap-mandatory">
                  <TabsTrigger 
                    value="personal" 
                    className="flex-shrink-0 px-3 sm:px-4 text-xs sm:text-sm max-w-[140px] sm:max-w-[200px] data-[state=active]:shadow-md transition-all snap-center"
                  >
                    Personal Info
                  </TabsTrigger>
                  <TabsTrigger 
                    value="work" 
                    className="flex-shrink-0 px-3 sm:px-4 text-xs sm:text-sm max-w-[140px] sm:max-w-[200px] data-[state=active]:shadow-md transition-all snap-center"
                  >
                    Work Details
                  </TabsTrigger>
                  <TabsTrigger 
                    value="email" 
                    className="flex-shrink-0 px-3 sm:px-4 text-xs sm:text-sm max-w-[140px] sm:max-w-[200px] data-[state=active]:shadow-md transition-all snap-center"
                  >
                    Email Prefs
                  </TabsTrigger>
                  <TabsTrigger 
                    value="sending" 
                    className="flex-shrink-0 px-3 sm:px-4 text-xs sm:text-sm max-w-[140px] sm:max-w-[200px] data-[state=active]:shadow-md transition-all snap-center"
                  >
                    Email Sending
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notifications" 
                    className="flex-shrink-0 px-3 sm:px-4 text-xs sm:text-sm max-w-[140px] sm:max-w-[200px] data-[state=active]:shadow-md transition-all snap-center"
                  >
                    Task Notifications
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="personal" className="animate-fade-in overflow-y-auto max-h-[calc(100vh-500px)] px-1" style={{ scrollbarGutter: 'stable' }}>
                  <Form {...personalForm}>
                    <form onSubmit={personalForm.handleSubmit(handleUpdatePersonalInfo)} className="space-y-6 pb-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={personalForm.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>First Name</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={personalForm.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Last Name</FormLabel>
                              <FormControl>
                                <Input {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <div className="space-y-2 pt-4 border-t border-border">
                        <Label htmlFor="email" className="font-semibold">Email</Label>
                        <Input 
                          id="email" 
                          type="email" 
                          value={user?.email || ''}
                          disabled
                          className="bg-muted/50"
                        />
                        <p className="text-xs text-muted-foreground">
                          Email cannot be changed
                        </p>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full sm:w-[180px] md:w-[200px] shadow-md hover:shadow-lg hover:scale-[1.02] transition-all"
                        disabled={isSubmittingPersonal}
                      >
                        {isSubmittingPersonal ? 'Saving...' : 'Save Personal Info'}
                      </Button>
                    </form>
                  </Form>
                </TabsContent>
                
                <TabsContent value="work" className="animate-fade-in overflow-y-auto max-h-[calc(100vh-500px)] px-1" style={{ scrollbarGutter: 'stable' }}>
                  <Form {...workForm}>
                    <form onSubmit={workForm.handleSubmit(handleUpdateWorkInfo)} className="space-y-6 pb-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={workForm.control}
                          name="jobTitle"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Job Title</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. Marketing Manager" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={workForm.control}
                          name="department"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Department</FormLabel>
                              <FormControl>
                                <Input placeholder="e.g. Marketing" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={workForm.control}
                          name="workEmail"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Work Email</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="work@example.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={workForm.control}
                          name="workPhone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Work Phone</FormLabel>
                              <FormControl>
                                <Input placeholder="+1 (555) 123-4567" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={workForm.control}
                        name="officeLocation"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Office Location</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. New York Office" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={workForm.control}
                        name="contactNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Contact Number</FormLabel>
                            <FormControl>
                              <Input placeholder="+1 (555) 987-6543" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={workForm.control}
                        name="linkedinUrl"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>LinkedIn URL</FormLabel>
                            <FormControl>
                              <Input placeholder="https://linkedin.com/in/username" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={workForm.control}
                        name="timezone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Timezone</FormLabel>
                            <Select 
                              onValueChange={field.onChange} 
                              defaultValue={field.value}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select your timezone" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {TIMEZONES.map((timezone) => (
                                  <SelectItem key={timezone.value} value={timezone.value}>
                                    {timezone.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={workForm.control}
                        name="bio"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Bio</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="A short description about yourself" 
                                className="min-h-[100px]" 
                                {...field} 
                              />
                            </FormControl>
                            <FormDescription>
                              Brief professional description (500 characters max)
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Button 
                        type="submit" 
                        className="w-full sm:w-[180px] md:w-[200px] shadow-md hover:shadow-lg hover:scale-[1.02] transition-all"
                        disabled={isSubmittingWork}
                      >
                        {isSubmittingWork ? 'Saving...' : 'Save Work Info'}
                      </Button>
                    </form>
                  </Form>
                </TabsContent>

                <TabsContent value="email" className="animate-fade-in overflow-y-auto max-h-[calc(100vh-500px)] px-1" style={{ scrollbarGutter: 'stable' }}>
                  <Form {...emailPreferencesForm}>
                    <form onSubmit={emailPreferencesForm.handleSubmit(handleUpdateEmailPreferences)} className="space-y-6 pb-4">
                      <FormField
                        control={emailPreferencesForm.control}
                        name="preferredFromName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Preferred From Name</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. John Smith" {...field} />
                            </FormControl>
                            <FormDescription>
                              This name will be used as the sender name when sending emails. Defaults to your full name.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="space-y-2">
                        <Label>Work Email (Sender Address)</Label>
                        <Input 
                          value={profile?.work_email || ''}
                          disabled
                        />
                        <p className="text-xs text-muted-foreground">
                          This email is used as your sender address. Update in Work Details tab.
                        </p>
                      </div>

                      <FormField
                        control={emailPreferencesForm.control}
                        name="emailSignature"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Signature</FormLabel>
                            <FormControl>
                              <Textarea 
                                placeholder="Best regards,&#10;John Smith&#10;Sales Manager"
                                rows={6}
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>
                              This signature will be automatically appended to your emails. {field.value?.length || 0}/1000 characters
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {(emailPreferencesForm.watch('preferredFromName') || emailPreferencesForm.watch('emailSignature')) && (
                        <div className="p-6 border border-border rounded-lg bg-white/[0.03] backdrop-blur-sm">
                          <h4 className="text-sm font-semibold mb-3">Email Preview</h4>
                          <div className="text-sm space-y-1">
                            <p className="text-muted-foreground">
                              <span className="font-semibold text-foreground">From:</span> {emailPreferencesForm.watch('preferredFromName') || `${profile?.first_name} ${profile?.last_name}`} &lt;{profile?.work_email || 'your-email@company.com'}&gt;
                            </p>
                            {emailPreferencesForm.watch('emailSignature') && (
                              <div className="mt-4 pt-4 border-t">
                                <p className="whitespace-pre-wrap text-xs leading-relaxed">{emailPreferencesForm.watch('emailSignature')}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <Button 
                        type="submit" 
                        className="w-full sm:w-[180px] md:w-[200px] shadow-md hover:shadow-lg hover:scale-[1.02] transition-all"
                        disabled={isSubmittingEmail}
                      >
                        {isSubmittingEmail ? 'Saving...' : 'Save Email Preferences'}
                      </Button>
                    </form>
                  </Form>
                </TabsContent>

                <TabsContent value="sending" className="animate-fade-in overflow-y-auto max-h-[calc(100vh-500px)] px-1" style={{ scrollbarGutter: 'stable' }}>
                  {profile?.company_id && (
                    <div className="space-y-6 pb-4">
                      <UserEmailConfiguration 
                        userId={user?.id || ''} 
                        companyId={profile.company_id}
                      />
                      <UserSenderIdentities 
                        userId={user?.id || ''} 
                        companyId={profile.company_id}
                      />
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="notifications" className="animate-fade-in overflow-y-auto max-h-[calc(100vh-500px)] px-1" style={{ scrollbarGutter: 'stable' }}>
                  <div className="pb-4">
                    <TaskEmailPreferences />
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
